import { Link, Breadcrumbs, Typography, Stack, TablePagination } from "@mui/material";
import { useCargosListaViewModel } from "../../viewModels/cargos_lista_view_model";
import { Header } from "./components/header";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateCargosListaColumns } from "./columns";
import { Cargo } from "../../../domain/models/cargo";
import { CopyCargoModal } from "./components/copy_cargo_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * CargosLista
 */

export const CargosLista = () => {
  const viewModel = useCargosListaViewModel();

  const columnsDef = useMemo(() => {
    return GenerateCargosListaColumns({
      onActionEdit: viewModel.handleCargoCadastroOpenState,
      onActionDelete: viewModel.handleCargoDeleteOpenState,
      onActionExportar: viewModel.handleCargoExportar,
      onActionCopy: viewModel.handleCargoCopyOpenState,
    });
  }, [
    viewModel.handleCargoCadastroOpenState,
    viewModel.handleCargoDeleteOpenState,
    viewModel.handleCargoExportar,
    viewModel.handleCargoCopyOpenState,
  ]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[10rem]">
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={viewModel.handleRelatorio}>
                  Gerar relatório
                </PrimaryButton>
              </div>

              <div className="w-[10rem] ">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleNewCargo(true);
                    } else {
                      viewModel.handleNewCargo(e.ctrlKey)
                    }
                    e.preventDefault();
                  }}>
                  Novo Cargo
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Cargos
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Cargos</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  nome={viewModel.filters.nome}
                  atribuicoes={viewModel.filters.atribuicoes}
                  escolaridade={viewModel.filters.escolaridade}
                  status={viewModel.filters.status}
                  onNomeChange={viewModel.handleNomeChange}
                  onAtribuicoesChange={viewModel.handleAtribuicoesChange}
                  onEscolaridadeChange={viewModel.handleEscolaridadeChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters} />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col flex-wrap justify-end">
                      <LegendComponent>
                        <LegendItemComponent
                          icon={<LegendIcon color='green' title='A' />}
                          label='Ativo' />

                        <LegendItemComponent
                          icon={<LegendIcon color='red' title='I' />}
                          label='Inativo' />

                      </LegendComponent>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="cargos_lista"
                    disableColumnMenu={false}
                    getRowId={(row: Cargo) => row.idCargo}
                    columns={columnsDef}
                    loading={viewModel.loading}
                    rows={viewModel.cargos}
                    rowCount={viewModel.cargos.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }} />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }

        <DeleteDialog
          open={viewModel.openDeleteModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleCargoDeleteOpenState(null, false)}
          title={`Você realmente deseja excluir este item?`}
          onDelete={viewModel.handleConfirmarDeletarCargo} />

        <CopyCargoModal
          open={viewModel.openCopyModal}
          saving={viewModel.saving}
          onSubmit={viewModel.handleCargoCopy}
          onClose={viewModel.handleCargoCopyOpenState}
        />

        <ReportViewerModal
          reportViewerState={viewModel.reportViewerState}
        />

      </div>
    </>
  );
}